import { useState, useEffect, useRef } from "react";
import SpotifyPlayer from "react-spotify-web-playback";
import '../styles/player.css';
import ShuffleButton from './ShuffleButton';
import RepeatButton from './RepeatButton';

export default function Player({ accessToken, trackUri, listensRef, pointsRef, duration }) {
  const [play, setPlay] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [halfPointsGiven, setHalfPointsGiven] = useState(false);
  const [fullPointsGiven, setFullPointsGiven] = useState(false);
  const [thirtySecondsPointsGiven, setThirtySecondsPointsGiven] = useState(false);
  const intervalRef = useRef(null);
  const [isShuffle, setIsShuffle] = useState(false);
  const [isRepeat, setIsRepeat] = useState(false);
  const durationInSeconds = Math.floor(duration / 1000);
  const [campaignPoints, setCampaignPoints] = useState({
    pointsFor30Sec: 0,
    pointsForHalf: 0,
    pointsForFull: 0,
  });

  const getCampaignPoints = async (trackUri) => {
    if (!trackUri) {
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACK4APP_SERVER_PATH}/api/get-campaign-points`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ trackUri }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      console.log("Campaign points data:", data);

      setCampaignPoints({
        pointsFor30Sec: data.pointsFor30Sec,
        pointsForHalf: data.pointsForHalf,
        pointsForFull: data.pointsForFull,
      });
    } catch (error) {
      console.error("Error fetching campaign points:", error);
      setCampaignPoints({
        pointsFor30Sec: 0,
        pointsForHalf: 0,
        pointsForFull: 0,
      });
    }
  };

  const addPoints = async (points) => {
    try {
      await fetch(`${process.env.REACT_APP_BACK4APP_SERVER_PATH}/add-points`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accessToken, points, trackUri }),
      });
      console.log(`Points added: ${points}`);
      if (pointsRef.current) {
        pointsRef.current.refreshPoints();
      }
    } catch (error) {
      console.error("Error adding points:", error);
    }
  };

  const addListens = async (listens) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACK4APP_SERVER_PATH}/add-listens`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ trackUri, listens }),
      });

      if (!response.ok) {
        throw new Error("Failed to add listens");
      }

      const data = await response.json();
      console.log(`Listens added:`, data);
    } catch (error) {
      console.error("Error adding listens:", error);
    }
  };


  const startInterval = () => {
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setElapsedTime((prevTime) => {
          const newTime = prevTime + 1;
          if (newTime >= durationInSeconds) {
            clearInterval(intervalRef.current);
          }
          return newTime;
        });
      }, 1000);
    }
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const calculatePoints = (elapsedTime) => {
    if (elapsedTime >= 30 && !thirtySecondsPointsGiven) {
      setThirtySecondsPointsGiven(true);
      addListens(1);
      return campaignPoints.pointsFor30Sec;
    }

    if (elapsedTime >= durationInSeconds / 2 && !halfPointsGiven) {
      setHalfPointsGiven(true);
      return campaignPoints.pointsForHalf;
    }

    if (elapsedTime >= (durationInSeconds - 1) && !fullPointsGiven) {
      setFullPointsGiven(true);
      return campaignPoints.pointsForFull;
    }

    return 0;
  };

  useEffect(() => {
    if (
      campaignPoints.pointsFor30Sec === 0 ||
      campaignPoints.pointsForHalf === 0 ||
      campaignPoints.pointsForFull === 0
    ) {
      const progressBar = document.querySelector('.slider');
      if (progressBar) {
        const existingMarkers = progressBar.querySelectorAll('.marker');
        existingMarkers.forEach(marker => {
          marker.remove();
        });
      }
      return;
    }

    const durationInSeconds = duration / 1000;
    const thresholds = [
      { percent: (30 / durationInSeconds) * 100, points: campaignPoints.pointsFor30Sec },
      { percent: 50, points: campaignPoints.pointsForHalf },
      { percent: 99, points: campaignPoints.pointsForFull },
    ];

    const svgs = {
      inactive: `
  <svg width="42" height="27" viewBox="0 0 42 27" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="0.5" y="0.5" width="41" height="26" rx="4.5" stroke="black"/>
  <circle cx="20.5" cy="13.5" r="5.5" fill="black"/>
  </svg>`,
      active: `
  <svg width="42" height="27" viewBox="0 0 42 27" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="0.5" y="0.5" width="41" height="26" rx="4.5" fill="black" stroke="#9FFF9A"/>
  <path d="M21 7C17.6862 7 15 9.6862 15 13C15 16.3138 17.6862 19 21 19C24.048 19 26.6064 16.7152 26.9628 13.7128C27.0012 13.3834 26.7666 13.0768 26.4372 13.0372C26.1084 12.9988 25.8012 13.2334 25.7628 13.5628C25.4778 15.9628 23.4384 17.8 21 17.8C18.3492 17.8 16.2 15.6508 16.2 13C16.2 10.3492 18.3492 8.2 21 8.2C21.5538 8.2 22.1022 8.3002 22.6128 8.4814C22.9248 8.5918 23.2518 8.4184 23.3628 8.1064C23.4732 7.7938 23.3184 7.44821 23.0064 7.33721C22.368 7.11101 21.6906 7 21 7ZM26.4 8.2C26.2464 8.2 26.0856 8.254 25.9686 8.3686L20.7372 13.525C20.583 13.6768 20.4276 13.6474 20.3064 13.4686L19.7064 12.5872C19.5228 12.3166 19.1376 12.238 18.8628 12.4186C18.5874 12.5992 18.51 12.973 18.6936 13.2436L19.2936 14.125C19.8342 14.9224 20.892 15.028 21.5814 14.35L26.8314 9.21279C27.0654 8.98239 27.0654 8.599 26.8314 8.3686C26.7144 8.2534 26.5536 8.2 26.4 8.2Z" fill="#9FFF9A"/>
  </svg>`,
    };

    const createMarker = (percent, points, isActive) => {
      const marker = document.createElement('div');
      marker.classList.add('marker');
      marker.style.left = `${percent}%`;
      marker.innerHTML = `${isActive ? svgs.active : svgs.inactive}<span>${points} Point</span>`;
      return marker;
    };

    const progressBar = document.querySelector('.slider');

    if (progressBar) {
      const existingMarkers = progressBar.querySelectorAll('.marker');
      const progressPercent = (elapsedTime / durationInSeconds) * 100;

      if (existingMarkers.length === 0) {
        thresholds.forEach(({ percent, points }) => {
          const isActive = progressPercent >= percent;
          const marker = createMarker(percent, points, isActive);
          progressBar.appendChild(marker);
        });
      } else {
        thresholds.forEach(({ percent, points }, index) => {
          const marker = existingMarkers[index];
          const isActive = progressPercent >= percent;
          marker.style.left = `${percent}%`;
          marker.innerHTML = `${isActive ? svgs.active : svgs.inactive}<span>${points} Point</span>`;
          marker.classList.toggle('active', isActive);
        });
      }
    }
  }, [elapsedTime, duration, campaignPoints]);

  useEffect(() => {
    setPlay(true);
    setElapsedTime(0);
    setHalfPointsGiven(false);
    setFullPointsGiven(false);
    setThirtySecondsPointsGiven(false);
    getCampaignPoints(trackUri);
    return stopInterval;
  }, [trackUri]);

  useEffect(() => {
    if (elapsedTime > 0) {
      const points = calculatePoints(elapsedTime);
      if (points > 0) {
        addPoints(points);
      }
    }
  }, [elapsedTime]);

  const toggleShuffle = () => {
    setIsShuffle((prev) => !prev);
    console.log('Shuffle toggled:', !isShuffle);
  };

  const toggleRepeat = () => {
    setIsRepeat((prev) => !prev);
    console.log('Repeat toggled:', !isRepeat);
  };

  if (!accessToken) return null;

  return (
    <SpotifyPlayer
      components={{
        leftButton: <ShuffleButton isShuffle={isShuffle} toggleShuffle={toggleShuffle} />,
        rightButton: <RepeatButton isRepeat={isRepeat} toggleRepeat={toggleRepeat} />,
      }}
      token={accessToken}
      showSaveIcon
      callback={(state) => {
        if (state.isPlaying) {
          setPlay(true);
          startInterval();
        } else {
          setPlay(false);
          stopInterval();
          if (state.position >= 100) {
            setElapsedTime(0);
            setHalfPointsGiven(false);
            setFullPointsGiven(false);
            setThirtySecondsPointsGiven(false);
            if (isRepeat) {
              setTimeout(() => {
                setPlay(true);
              }, 1000);
            }
          }
        }
      }}
      play={play}
      repeat={isRepeat ? 'track' : 'off'}
      uris={trackUri ? [trackUri] : []}
      magnifySliderOnHover={false}
    />
  );
}